import React, { useState, useEffect } from "react";
import {
    login,
    completeTwoFactorLogin,
    startPending2FASetup,
    verifyPending2FASetup,
    resendTwoFactorLoginCode,
    resendPending2FASetupCode,
    forgotPassword,
    setRememberedTwoFactorToken,
} from "./api";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";

function Login({ onLoginSuccess }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [twoFACode, setTwoFACode] = useState("");
    const [twoFactorToken, setTwoFactorToken] = useState("");
    const [twoFactorMethod, setTwoFactorMethod] = useState("totp");
    const [needs2FA, setNeeds2FA] = useState(false);
    const [needs2FASetup, setNeeds2FASetup] = useState(false);
    const [twoFASetupToken, setTwoFASetupToken] = useState("");
    const [twoFASetupQrDataUrl, setTwoFASetupQrDataUrl] = useState("");
    const [twoFASetupMethod, setTwoFASetupMethod] = useState("");
    const [twoFASetupOptions, setTwoFASetupOptions] = useState([]);
    const [twoFASetupComplete, setTwoFASetupComplete] = useState(false);
    const [error, setError] = useState("");
    const [infoMessage, setInfoMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotUsername, setForgotUsername] = useState("");

    // ---- DARK MODE DETECTION ----
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;

        const mq = window.matchMedia("(prefers-color-scheme: dark)");

        const updateMode = () => {
            setIsDark(mq.matches);
        };

        updateMode(); // initial

        if (mq.addEventListener) {
            mq.addEventListener("change", updateMode);
        } else if (mq.addListener) {
            mq.addListener(updateMode);
        }

        return () => {
            if (mq.removeEventListener) {
                mq.removeEventListener("change", updateMode);
            } else if (mq.removeListener) {
                mq.removeListener(updateMode);
            }
        };
    }, []);

    // 🔧 Keep the real page background in sync so scrolling doesn't show white
    useEffect(() => {
        if (typeof document === "undefined") return;

        const previousBg = document.body.style.backgroundColor;
        document.body.style.backgroundColor = isDark ? "#020617" : "#f3f4f6";

        return () => {
            document.body.style.backgroundColor = previousBg;
        };
    }, [isDark]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const hash = String(window.location.hash || "");
        const queryIndex = hash.indexOf("?");
        if (queryIndex < 0) return;
        const query = hash.slice(queryIndex + 1);
        const params = new URLSearchParams(query);
        const authMessage = String(params.get("auth_message") || "").trim();
        if (!authMessage) return;

        setInfoMessage(authMessage);
        const cleanHash = hash.slice(0, queryIndex) || "#/dashboard";
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${cleanHash}`);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setInfoMessage("");
        setLoading(true);

        try {
            if (!needs2FA && !needs2FASetup) {
                const data = await login(username, password);

                if (!data || !data.ok) {
                    setError(data?.message || "Login failed");
                    setLoading(false);
                    return;
                }

                if (data.requires2fa) {
                    setNeeds2FA(true);
                    setTwoFactorToken(data.twoFactorToken);
                    setTwoFactorMethod(
                        String(data.twoFactorMethod || "totp").toLowerCase()
                    );
                    setLoading(false);
                    return;
                }

                if (data.requires2fa_setup && data.setupToken) {
                    const options = Array.isArray(data.twoFactorSetupOptions)
                        ? data.twoFactorSetupOptions
                        : ["totp"];
                    setNeeds2FASetup(true);
                    setTwoFASetupToken(data.setupToken);
                    setTwoFASetupOptions(options);
                    setTwoFASetupQrDataUrl("");
                    setTwoFASetupMethod("");

                    // For admin-only flow (totp only), start immediately.
                    if (options.length === 1 && options[0] === "totp") {
                        const setupRes = await startPending2FASetup(
                            data.setupToken,
                            "totp"
                        );
                        if (!setupRes?.ok) {
                            setNeeds2FASetup(false);
                            setTwoFASetupToken("");
                            setTwoFASetupQrDataUrl("");
                            setError(setupRes?.message || "Failed to start 2FA setup");
                            setLoading(false);
                            return;
                        }
                        setTwoFASetupMethod("totp");
                        setTwoFASetupQrDataUrl(setupRes.qrDataUrl || "");
                    }

                    setLoading(false);
                    return;
                }

                if (data.token) {
                    if (data.rememberTwoFactorToken) {
                        setRememberedTwoFactorToken(data.rememberTwoFactorToken);
                    }
                    localStorage.setItem("token", data.token);
                    onLoginSuccess(data.user);
                    return;
                }

                setError("Unexpected login response");
            } else if (needs2FA) {
                const data = await completeTwoFactorLogin(
                    twoFACode,
                    twoFactorToken
                );

                if (!data || !data.ok || !data.token) {
                    setError(data?.message || "2FA verification failed");
                    setLoading(false);
                    return;
                }

                if (data.rememberTwoFactorToken) {
                    setRememberedTwoFactorToken(data.rememberTwoFactorToken);
                }
                localStorage.setItem("token", data.token);
                onLoginSuccess(data.user);
            } else if (needs2FASetup) {
                if (!twoFASetupMethod) {
                    setError("Choose a 2FA method to continue.");
                    setLoading(false);
                    return;
                }
                const data = await verifyPending2FASetup(
                    twoFASetupToken,
                    twoFACode,
                    twoFASetupMethod
                );

                if (!data || !data.ok || !data.token) {
                    setError(data?.message || "2FA setup verification failed");
                    setLoading(false);
                    return;
                }
                if (data.rememberTwoFactorToken) {
                    setRememberedTwoFactorToken(data.rememberTwoFactorToken);
                }
                setNeeds2FASetup(false);
                setTwoFASetupToken("");
                setTwoFASetupQrDataUrl("");
                setTwoFASetupMethod("");
                setTwoFASetupOptions([]);
                setTwoFACode("");
                setInfoMessage("");
                localStorage.setItem("token", data.token);
                onLoginSuccess(data.user);
                return;
            }
        } catch (err) {
            console.error(err);
            setError("Error during login");
        } finally {
            setLoading(false);
        }
    };

    // ---- THEMED STYLES (light / dark) ----
    const pageWrapper = {
        ...pageWrapperBase,
        background: isDark ? "#020617" : "#f3f4f6",
        color: isDark ? "#e5e7eb" : "#111827",
    };

    const card = {
        ...cardBase,
        background: isDark ? "#020617" : "#ffffff",
        border: isDark ? "1px solid #1f2937" : "none",
        boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.7)"
            : "0 20px 60px rgba(15,23,42,0.15)",
    };

    const errorStyle = {
        ...errorStyleBase,
        background: isDark ? "#7f1d1d" : "#fee2e2",
        color: isDark ? "#fecaca" : "#b91c1c",
        border: isDark ? "1px solid #fca5a5" : "none",
    };

    const labelText = {
        ...labelTextBase,
        color: isDark ? "#e5e7eb" : "#4b5563",
    };

    const inputStyle = {
        ...inputStyleBase,
        background: isDark ? "#020617" : "#ffffff",
        border: isDark ? "1px solid #334155" : "1px solid #d1d5db",
        color: isDark ? "#e5e7eb" : "#111827",
    };

    const description = {
        ...descriptionBase,
        color: isDark ? "#9ca3af" : "#4b5563",
    };

    const buttonPrimary = {
        ...buttonPrimaryBase,
        background: isDark ? "#2563eb" : "#1d3557",
        opacity: loading ? 0.8 : 1,
    };

    const buttonMethod = {
        ...buttonPrimary,
        width: "100%",
    };

    const beginSetupMethod = async (method) => {
        setError("");
        setInfoMessage("");
        setLoading(true);
        try {
            const setupRes = await startPending2FASetup(twoFASetupToken, method);
            if (!setupRes?.ok) {
                setError(setupRes?.message || "Failed to start 2FA setup");
                return;
            }
            setTwoFASetupMethod(method);
            setTwoFASetupQrDataUrl(method === "totp" ? setupRes.qrDataUrl || "" : "");
            setTwoFACode("");
            if (method === "email") {
                setInfoMessage(
                    setupRes?.message || "Verification code sent to your email."
                );
            }
        } catch {
            setError("Failed to start 2FA setup");
        } finally {
            setLoading(false);
        }
    };

    const resetToSignIn = () => {
        setTwoFASetupComplete(false);
        setError("");
        setInfoMessage("");
        setPassword("");
        setTwoFACode("");
        setNeeds2FA(false);
        setNeeds2FASetup(false);
        setTwoFactorToken("");
        setTwoFactorMethod("totp");
        setTwoFASetupToken("");
        setTwoFASetupQrDataUrl("");
        setTwoFASetupMethod("");
        setTwoFASetupOptions([]);
        setShowForgot(false);
    };

    return (
        <div style={pageWrapper}>
            <div style={card}>
                <div style={headerBox}>
                    <img
                        src={isDark ? logoLight : logoDark}
                        alt="Urban Podcasts"
                        style={logoImage}
                    />
                </div>

                {error && <div style={errorStyle}>{error}</div>}

                {twoFASetupComplete ? (
                    <div style={formStyle}>
                        <p style={description}>
                            Two-factor authentication has been enabled. Please log in again to continue.
                        </p>
                        <button
                            type="button"
                            style={buttonPrimary}
                            onClick={resetToSignIn}
                        >
                            Back to sign in
                        </button>
                    </div>
                ) : !needs2FA && !needs2FASetup ? (
                    showForgot ? (
                        <form style={formStyle}>
                            <label style={labelStyle}>
                                <span style={labelText}>
                                    Email for password reset
                                </span>
                                <input
                                    value={forgotUsername}
                                    onChange={(e) =>
                                        setForgotUsername(e.target.value)
                                    }
                                    type="email"
                                    autoComplete="email"
                                    style={inputStyle}
                                />
                                <button
                                    type="button"
                                    style={buttonSecondary}
                                    disabled={loading}
                                    onClick={async () => {
                                        setError("");
                                        setInfoMessage("");
                                        const usernameToUse = String(
                                            forgotUsername || username
                                        ).trim();
                                        if (!usernameToUse) {
                                            setError("Enter your email first.");
                                            return;
                                        }
                                        setLoading(true);
                                        try {
                                            const data = await forgotPassword(
                                                usernameToUse
                                            );
                                            if (!data?.ok) {
                                                setError(
                                                    data?.message ||
                                                        "Failed to request reset link"
                                                );
                                                return;
                                            }
                                            setInfoMessage(
                                                data.message ||
                                                    "If the account exists, a reset link has been emailed."
                                            );
                                        } catch (err) {
                                            setError("Failed to request reset link");
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    Send reset link
                                </button>
                            </label>
                            {infoMessage ? (
                                <div style={infoStyle}>{infoMessage}</div>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForgot(false);
                                    setInfoMessage("");
                                    setError("");
                                }}
                                style={textButton}
                            >
                                Back
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleSubmit} style={formStyle}>
                            <label style={labelStyle}>
                                <span style={labelText}>Email</span>
                                <input
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    type="email"
                                    autoComplete="email"
                                    required
                                    style={inputStyle}
                                />
                            </label>

                            <label style={labelStyle}>
                                <span style={labelText}>Password</span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    style={inputStyle}
                                />
                            </label>

                            {infoMessage ? (
                                <div style={infoStyle}>{infoMessage}</div>
                            ) : null}

                            <button
                                type="submit"
                                disabled={loading}
                                style={buttonPrimary}
                            >
                                {loading ? "Logging in…" : "Login"}
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setShowForgot(true);
                                    setInfoMessage("");
                                    setError("");
                                }}
                                style={textButton}
                            >
                                Forgot password?
                            </button>
                        </form>
                    )
                ) : needs2FA ? (
                    <form onSubmit={handleSubmit} style={formStyle}>
                        <p style={description}>
                            {twoFactorMethod === "email"
                                ? "Enter the 6-digit code sent to your email address."
                                : "Two-factor authentication is enabled for this account. Enter the 6-digit code from your authenticator app."}
                        </p>

                        <label style={labelStyle}>
                            <span style={labelText}>6-digit code</span>
                            <input
                                type="text"
                                value={twoFACode}
                                onChange={(e) =>
                                    setTwoFACode(e.target.value)
                                }
                                maxLength={6}
                                required
                                style={{
                                    ...inputStyle,
                                    width: "160px",
                                    textAlign: "center",
                                }}
                            />
                        </label>
                        {twoFactorMethod === "email" ? (
                            <button
                                type="button"
                                style={buttonSecondary}
                                disabled={loading}
                                onClick={async () => {
                                    setError("");
                                    setInfoMessage("");
                                    setLoading(true);
                                    try {
                                        const data = await resendTwoFactorLoginCode(
                                            twoFactorToken
                                        );
                                        if (!data?.ok) {
                                            setError(
                                                data?.message ||
                                                    "Failed to resend verification code"
                                            );
                                            return;
                                        }
                                        setInfoMessage(
                                            data?.message ||
                                                "A new verification code has been sent."
                                        );
                                    } catch {
                                        setError("Failed to resend verification code");
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                Resend code
                            </button>
                        ) : null}

                        <button
                            type="submit"
                            disabled={loading}
                            style={buttonPrimary}
                        >
                            {loading ? "Verifying…" : "Verify code"}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit} style={formStyle}>
                        {!twoFASetupMethod ? (
                            <>
                                <p style={description}>
                                    Two-factor authentication is required. Choose how
                                    you want to secure your account.
                                </p>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 10,
                                    }}
                                >
                                    {twoFASetupOptions.includes("totp") ? (
                                        <button
                                            type="button"
                                            style={buttonMethod}
                                            disabled={loading}
                                            onClick={() => beginSetupMethod("totp")}
                                        >
                                            Use Authenticator App
                                        </button>
                                    ) : null}
                                    {twoFASetupOptions.includes("email") ? (
                                        <button
                                            type="button"
                                            style={buttonMethod}
                                            disabled={loading}
                                            onClick={() => beginSetupMethod("email")}
                                        >
                                            Use Email Code
                                        </button>
                                    ) : null}
                                </div>
                            </>
                        ) : null}

                        {twoFASetupMethod === "totp" ? (
                            <>
                                <p style={description}>
                                    Scan this QR code with your authenticator app and
                                    enter the 6-digit code to continue.
                                </p>

                                {twoFASetupQrDataUrl ? (
                                    <div
                                        style={{
                                            marginTop: 4,
                                            marginBottom: 4,
                                            textAlign: "center",
                                        }}
                                    >
                                        <img
                                            src={twoFASetupQrDataUrl}
                                            alt="2FA setup QR code"
                                            style={{
                                                width: 200,
                                                height: 200,
                                                borderRadius: 8,
                                                border: isDark
                                                    ? "1px solid #334155"
                                                    : "1px solid #e5e7eb",
                                                background: "#fff",
                                                padding: 8,
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <p style={description}>Preparing QR code…</p>
                                )}
                            </>
                        ) : null}

                        {twoFASetupMethod === "email" ? (
                            <>
                                <p style={description}>
                                    Enter the 6-digit code sent to your email.
                                </p>
                                <button
                                    type="button"
                                    style={buttonSecondary}
                                    disabled={loading}
                                    onClick={async () => {
                                        setError("");
                                        setInfoMessage("");
                                        setLoading(true);
                                        try {
                                            const data =
                                                await resendPending2FASetupCode(
                                                    twoFASetupToken,
                                                    "email"
                                                );
                                            if (!data?.ok) {
                                                setError(
                                                    data?.message ||
                                                        "Failed to resend verification code"
                                                );
                                                return;
                                            }
                                            setInfoMessage(
                                                data?.message ||
                                                    "A new verification code has been sent."
                                            );
                                        } catch {
                                            setError("Failed to resend verification code");
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    Resend code
                                </button>
                            </>
                        ) : null}

                        {twoFASetupMethod ? (
                            <>
                                <label style={labelStyle}>
                                    <span style={labelText}>6-digit code</span>
                                    <input
                                        type="text"
                                        value={twoFACode}
                                        onChange={(e) => setTwoFACode(e.target.value)}
                                        maxLength={6}
                                        required
                                        style={{
                                            ...inputStyle,
                                            width: "160px",
                                            textAlign: "center",
                                        }}
                                    />
                                </label>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={buttonPrimary}
                                >
                                    {loading
                                        ? "Enabling…"
                                        : "Enable 2FA and continue"}
                                </button>
                                {twoFASetupOptions.length > 1 ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTwoFASetupMethod("");
                                            setTwoFACode("");
                                            setTwoFASetupQrDataUrl("");
                                            setInfoMessage("");
                                            setError("");
                                        }}
                                        style={textButton}
                                    >
                                        Choose another method
                                    </button>
                                ) : null}
                            </>
                        ) : null}
                    </form>
                )}
            </div>
        </div>
    );
}

/* ---------- Shared Font ---------- */
const fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/* ---------- Base Styles (theme-neutral) ---------- */

const pageWrapperBase = {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    fontFamily,
};

const cardBase = {
    width: "100%",
    maxWidth: "420px",
    padding: "28px 32px",
    borderRadius: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    fontFamily,
};

const headerBox = {
    textAlign: "center",
    marginBottom: 8,
    fontFamily,
};

const logoImage = {
    width: 220,
    maxWidth: "100%",
    height: "auto",
    margin: "0 auto 8px",
    display: "block",
};

const errorStyleBase = {
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily,
};

const formStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    fontFamily,
};

const labelStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontFamily,
};

const labelTextBase = {
    fontSize: 13,
    fontWeight: 600,
    fontFamily,
};

const inputStyleBase = {
    borderRadius: 999,
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    fontFamily,
};

const descriptionBase = {
    fontSize: 14,
    marginBottom: 4,
    fontFamily,
};

const buttonPrimaryBase = {
    marginTop: 4,
    padding: "10px 16px",
    borderRadius: 999,
    border: "none",
    color: "#ffffff",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily,
};

const buttonSecondary = {
    marginTop: 4,
    padding: "10px 16px",
    borderRadius: 999,
    border: "none",
    background: "#1d3557",
    color: "#ffffff",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
    width: "100%",
    fontFamily,
};

const infoStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    background: "#1e3a8a",
    color: "#dbeafe",
    fontSize: 13,
};

const textButton = {
    border: "none",
    background: "transparent",
    color: "#93c5fd",
    fontSize: 13,
    cursor: "pointer",
    padding: 0,
    width: "fit-content",
};

export default Login;
